import { useMemo } from "react";

import type { AiSessionFeedbackReason } from "@simplyk/common";

import { ChatView } from "../ChatView/ChatView";

import { ORGANIZATION_ASSISTANT_CONFIG } from "./organizationAssistantConfig";
import { OrganizationAssistantChat, useOrganizationAssistant } from "./useOrganizationAssistant";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { ASSISTANT_CONVERSATION_QUERY_OPTIONS } from "@/features/Assistant/helpers/conversationQueryOptions";
import { createConversationStorage } from "@/features/Assistant/helpers/conversationStorage";
import type { AssistantChatAdapter, AssistantChatControls } from "@/features/Assistant/helpers/types";

const ORGANIZATION_SESSION_STORAGE_KEY = "zeffy:organization-assistant:session";

const useOrganizationScope = () => {
  const { currentUser, organization } = useCurrentUserContext();
  const userId = currentUser?.id;
  const organizationId = organization?.id;
  return useMemo(
    () =>
      userId && organizationId
        ? {
            key: `${userId}:${organizationId}`,
            storage: createConversationStorage({
              key: `${ORGANIZATION_SESSION_STORAGE_KEY}:${userId}`,
              scope: organizationId,
            }),
          }
        : null,
    [userId, organizationId]
  );
};

const useOrganizationConversation = (aiSessionId: string | null, { enabled }: { enabled: boolean }) => {
  const { organization } = useCurrentUserContext();
  const organizationId = organization?.id;
  return trpc.getOrganizationAssistantConversation.useQuery(
    { organizationId: organizationId ?? "", aiSessionId: aiSessionId ?? "" },
    { ...ASSISTANT_CONVERSATION_QUERY_OPTIONS, enabled: enabled && Boolean(organizationId && aiSessionId) }
  );
};

const useOrganizationFeedbackMutations = () => {
  const { organization } = useCurrentUserContext();
  const organizationId = organization?.id ?? "";
  const rateMutation = trpc.rateOrganizationAssistantConversation.useMutation();
  const { mutateAsync: showFeedback } = trpc.showOrganizationAssistantFeedback.useMutation();
  return {
    rate: {
      mutateAsync: (input: {
        aiSessionId: string;
        rating: number;
        comment?: string;
        reason?: AiSessionFeedbackReason;
      }) => rateMutation.mutateAsync({ ...input, organizationId }),
      isPending: rateMutation.isPending,
      variables: rateMutation.variables,
    },
    show: (input: { aiSessionId: string }) => showFeedback({ ...input, organizationId }),
  };
};

export const ORGANIZATION_CHAT_ADAPTER: AssistantChatAdapter<OrganizationAssistantChat, AssistantChatControls> = {
  config: ORGANIZATION_ASSISTANT_CONFIG,
  useScope: useOrganizationScope,
  useConversation: useOrganizationConversation,
  useAssistant: useOrganizationAssistant,
  useFeedbackMutations: useOrganizationFeedbackMutations,
  toControls: (base) => base,
  ChatView,
};
